// 6 classic tarot spreads with overview, layout positions (x/y % within diagram), and meanings.
// Coordinates are CSS-friendly (top-left origin). Cards centred on (x, y).
export const SPREADS = [
  {
    id: "ppf",
    name: "Past · Present · Future",
    overview: "A simple three-card spread that gives a clear timeline read on any situation. Ideal for quick clarity.",
    bestFor: "Quick questions · timeline of a situation · daily reflection",
    cards: 3,
    aspect: "2/1",
    positions: [
      { n: 1, label: "Past",    meaning: "Influences that have shaped the current moment — patterns, memories, lessons.", x: 18, y: 50 },
      { n: 2, label: "Present", meaning: "Where you stand right now — energies, opportunities, challenges in play.",    x: 50, y: 50 },
      { n: 3, label: "Future",  meaning: "The likely direction if you continue on your current path.",                  x: 82, y: 50 },
    ],
  },
  {
    id: "celtic-cross",
    name: "The Celtic Cross",
    overview: "The most famous and comprehensive tarot spread — a 10-card reading that maps every dimension of a question.",
    bestFor: "Deep, complex questions · life crossroads · full situation analysis",
    cards: 10,
    aspect: "5/4",
    positions: [
      { n: 1,  label: "The Present",      meaning: "The heart of the matter — what's happening right now.",            x: 32, y: 50 },
      { n: 2,  label: "The Challenge",    meaning: "What crosses you — the immediate obstacle or tension.",            x: 32, y: 50, rotate: 90 },
      { n: 3,  label: "The Foundation",   meaning: "Distant past or root cause — what underpins this.",                x: 32, y: 82 },
      { n: 4,  label: "The Past",         meaning: "Recent events that have led here.",                                x: 12, y: 50 },
      { n: 5,  label: "The Crown",        meaning: "Your conscious goal, hope, or what you're aiming for.",            x: 32, y: 18 },
      { n: 6,  label: "The Near Future",  meaning: "What is about to unfold.",                                         x: 52, y: 50 },
      { n: 7,  label: "Yourself",         meaning: "Your attitude and energy in the situation.",                       x: 80, y: 82 },
      { n: 8,  label: "Environment",      meaning: "How others and external forces are influencing things.",           x: 80, y: 62 },
      { n: 9,  label: "Hopes & Fears",    meaning: "The inner emotions colouring the situation.",                      x: 80, y: 38 },
      { n: 10, label: "The Outcome",      meaning: "The likely culmination of the path ahead.",                        x: 80, y: 18 },
    ],
  },
  {
    id: "horseshoe",
    name: "The Horseshoe",
    overview: "A 7-card spread shaped like a horseshoe — flowing from past through present to outcome with extra context.",
    bestFor: "Yes/no with nuance · weighing a decision · understanding your influences",
    cards: 7,
    aspect: "2/1",
    positions: [
      { n: 1, label: "Past",                meaning: "What has led you here.",                          x: 10, y: 80 },
      { n: 2, label: "Present",             meaning: "Where you stand now.",                            x: 22, y: 48 },
      { n: 3, label: "Hidden Influences",   meaning: "Energies operating beneath the surface.",         x: 36, y: 22 },
      { n: 4, label: "Obstacles",           meaning: "What may stand in your way.",                     x: 50, y: 12 },
      { n: 5, label: "External Influences", meaning: "Other people and the wider environment.",        x: 64, y: 22 },
      { n: 6, label: "Suggested Action",    meaning: "The recommended approach.",                       x: 78, y: 48 },
      { n: 7, label: "Likely Outcome",      meaning: "Where this path naturally leads.",                x: 90, y: 80 },
    ],
  },
  {
    id: "relationship",
    name: "The Relationship Spread",
    overview: "A 7-card spread that mirrors two souls side by side — illuminating how each person feels, what binds them, and where the connection is heading.",
    bestFor: "Love & partnership · friendships · family dynamics · understanding 'us'",
    cards: 7,
    aspect: "1/1",
    positions: [
      { n: 1, label: "You",            meaning: "Your energy in the relationship.",            x: 22, y: 18 },
      { n: 2, label: "Them",           meaning: "Their energy in the relationship.",           x: 78, y: 18 },
      { n: 3, label: "Your Feelings",  meaning: "What lives in your heart toward them.",       x: 22, y: 45 },
      { n: 4, label: "Their Feelings", meaning: "What lives in their heart toward you.",       x: 78, y: 45 },
      { n: 5, label: "Strength",       meaning: "What is working — the gift of this bond.",    x: 50, y: 60 },
      { n: 6, label: "Challenge",      meaning: "What needs attention or healing.",            x: 50, y: 78 },
      { n: 7, label: "Direction",      meaning: "Where the connection is heading next.",       x: 50, y: 92 },
    ],
  },
  {
    id: "star",
    name: "The Star Spread",
    overview: "A 5-card spread shaped like a five-pointed star — exploring hope, intuition, and the higher guidance available to you.",
    bestFor: "Spiritual clarity · finding hope · soul-level questions · receiving guidance",
    cards: 5,
    aspect: "1/1",
    positions: [
      { n: 1, label: "What You Hope For", meaning: "Your highest wish for this area of life.", x: 50, y: 14 },
      { n: 2, label: "What You Fear",     meaning: "The shadow side — what holds you back.",   x: 88, y: 42 },
      { n: 3, label: "Your Strengths",    meaning: "Inner gifts you can draw upon.",           x: 72, y: 88 },
      { n: 4, label: "Guidance",          meaning: "The wisdom your higher self is offering.", x: 28, y: 88 },
      { n: 5, label: "Outcome",           meaning: "Where this energy can take you when followed.", x: 12, y: 42 },
    ],
  },
  {
    id: "zodiac",
    name: "The Zodiac Spread",
    overview: "A 12-card year-ahead reading — one card for each astrological house, mapping the months and themes of the year to come.",
    bestFor: "Yearly preview · birthdays · new year planning · big-picture life themes",
    cards: 12,
    aspect: "1/1",
    positions: (() => {
      const r = 38; const cx = 50; const cy = 50;
      const labels = [
        "1st — Self", "2nd — Resources", "3rd — Mind", "4th — Home",
        "5th — Creativity", "6th — Daily Life", "7th — Partnership", "8th — Transformation",
        "9th — Wisdom", "10th — Career", "11th — Community", "12th — Spirit",
      ];
      const meanings = [
        "Your identity and the year's overall energy.",
        "Money, values, and what you build on.",
        "Communication, learning, short journeys.",
        "Family, roots, your inner sanctuary.",
        "Play, romance, self-expression.",
        "Work, health, daily rhythm.",
        "Close relationships and 'others'.",
        "Shared resources, intimacy, change.",
        "Travel, philosophy, higher learning.",
        "Public role, ambition, calling.",
        "Friends, hopes, collective dreams.",
        "The unseen, intuition, release.",
      ];
      return Array.from({ length: 12 }, (_, i) => {
        const ang = (-90 + i * 30) * Math.PI / 180;
        return {
          n: i + 1,
          label: labels[i],
          meaning: meanings[i],
          x: +(cx + r * Math.cos(ang)).toFixed(2),
          y: +(cy + r * Math.sin(ang)).toFixed(2),
        };
      });
    })(),
  },
];
